/**
 * CR-56 P2, D104 — the two dates a payable invoice shows, from the due rule.
 *
 * Pure, and in `contracts` because A-40's form (which defaults them as Admin types) and the API
 * (which has always derived the stored due date) must read ONE definition. The API's own
 * `dueDateFor()` is pinned to this by a test; a change to either that the other does not follow
 * fails there.
 *
 *   MONTH_END { graceDays: n }   fallsDue       the last day of the issue date's month
 *                                lastBeforeLate that day plus n
 *   NET_DAYS  { days: n }        fallsDue       the issue date plus n
 *                                lastBeforeLate **null** — there is no grace, so there is ONE date
 *
 * **NET_DAYS has no grace component and none is invented here** to make the two shapes symmetrical
 * (D104): a portal with one date shows one date. `null` is that answer, not a missing value.
 *
 * **Which date is stored.** `mki_purchase_invoices.due_on` — the date overdue is read against — is the
 * LAST date before late (`dueOn`), because that is what `dueDateFor()` has always stored for
 * MONTH_END: month end plus grace (GCC's "month end plus 7", FN-05). `fallsDue` for a MONTH_END
 * rule is a display figure for the document, not a second due date.
 *
 * **This takes a rule, and does not decide whose.** The caller passes the rule in force — the
 * account's own term where it has one (D75), the portal's otherwise. Nothing here climbs that
 * ladder (`resolveDueRule()` does, and is untouched by this change).
 *
 * Dates are calendar days (`YYYY-MM-DD`) in the portal's zone (D49) and the arithmetic is on UTC
 * midnights, so a daylight-saving change cannot move a date by a day.
 */
export type PaymentWindowRule =
  | { readonly type: 'MONTH_END'; readonly graceDays: number }
  | { readonly type: 'NET_DAYS'; readonly days: number };

export interface PaymentWindow {
  /** When the invoice falls due. */
  readonly fallsDue: string;
  /** The last date before it is late. Null for NET_DAYS: one date, not a range. */
  readonly lastBeforeLate: string | null;
  /** The date `due_on` is defaulted to and stored as: `lastBeforeLate ?? fallsDue`. */
  readonly dueOn: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function plusDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Day 0 of the next month is the last day of this one — no leap-year or 31st special case. */
function monthEnd(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}

/** Null for anything that is not a `YYYY-MM-DD` day, so a half-typed date shows nothing, not a wrong one. */
export function paymentWindow(rule: PaymentWindowRule, issuedOn: string): PaymentWindow | null {
  // Round-trip, so an impossible day (2026-02-30) is refused instead of being rolled into March.
  const parsed = new Date(`${issuedOn}T00:00:00Z`);
  if (!DAY.test(issuedOn) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== issuedOn) return null;
  if (rule.type === 'NET_DAYS') {
    const fallsDue = plusDays(issuedOn, rule.days);
    return { fallsDue, lastBeforeLate: null, dueOn: fallsDue };
  }
  const fallsDue = monthEnd(issuedOn);
  const lastBeforeLate = plusDays(fallsDue, rule.graceDays);
  return { fallsDue, lastBeforeLate, dueOn: lastBeforeLate };
}
